/**
 * Pantalla 3, detalle (docs/FASE-1.md §6): manifiesto de un módulo en solo lectura (roles,
 * features, límites) y pestañas Planes / Clientes OAuth / Webhooks.
 */
import type { Module } from "@continnua/contracts";
import { useEffect, useState, type ReactElement } from "react";
import { Link, useParams } from "react-router-dom";

import { modulesApi } from "../api/client";
import { Badge, toneForStatus } from "../components/Badge";
import { Tabs, useTabs } from "../components/Tabs";
import { toDisplayMessage } from "../lib/errors";
import { ModuleClients } from "./Clients";
import { ModulePlans } from "./Plans";
import { ModuleWebhooks } from "./Webhooks";

const STATUS_LABEL: Record<string, string> = { active: "Activo", hidden: "Oculto", retired: "Retirado" };

function ManifestTab({ module }: { module: Module }): ReactElement {
  return (
    <section>
      <h2>Manifiesto</h2>
      <p className="subtle">
        URL base: <a href={module.baseUrl}>{module.baseUrl}</a> · Versión {module.manifestVersion}
      </p>
      {module.description && <p>{module.description}</p>}

      <h3>Roles</h3>
      {module.roles.length === 0 ? (
        <p className="subtle">Sin roles declarados.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Código</th>
              <th scope="col">Nombre</th>
              <th scope="col">Admin</th>
              <th scope="col">Descripción</th>
            </tr>
          </thead>
          <tbody>
            {module.roles.map((role) => (
              <tr key={role.code}>
                <td>{role.code}</td>
                <td>{role.name}</td>
                <td>{role.isAdmin ? "Sí" : "No"}</td>
                <td>{role.description ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Features</h3>
      {module.features.length === 0 ? (
        <p className="subtle">Sin features declaradas.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Clave</th>
              <th scope="col">Nombre</th>
              <th scope="col">Tipo</th>
              <th scope="col">Descripción</th>
            </tr>
          </thead>
          <tbody>
            {module.features.map((feature) => (
              <tr key={feature.key}>
                <td>{feature.key}</td>
                <td>{feature.name}</td>
                <td>{feature.valueType}</td>
                <td>{feature.description ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Límites</h3>
      {module.limits.length === 0 ? (
        <p className="subtle">Sin límites declarados.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Clave</th>
              <th scope="col">Nombre</th>
              <th scope="col">Unidad</th>
              <th scope="col">Agregación</th>
            </tr>
          </thead>
          <tbody>
            {module.limits.map((limit) => (
              <tr key={limit.key}>
                <td>{limit.key}</td>
                <td>{limit.name}</td>
                <td>{limit.unit}</td>
                <td>{limit.aggregation === "gauge" ? "Instantánea" : "Acumulado mensual"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Eventos de webhook suscritos</h3>
      <p>{module.webhookEvents.length > 0 ? module.webhookEvents.join(", ") : "Ninguno."}</p>
    </section>
  );
}

export function ModuleDetail(): ReactElement {
  const { code } = useParams<{ code: string }>();
  const [module, setModule] = useState<Module | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useTabs("manifest");

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    modulesApi
      .get(code)
      .then((data) => {
        if (!cancelled) setModule(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(toDisplayMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (!code) return <p className="field-error">Falta el código del módulo.</p>;

  if (error) {
    return (
      <section>
        <p className="field-error" role="alert">
          {error}
        </p>
        <Link to="/modules">Volver a módulos</Link>
      </section>
    );
  }

  if (!module) return <p>Cargando…</p>;

  return (
    <section>
      <p>
        <Link to="/modules">← Módulos</Link>
      </p>
      <div className="page-header">
        <h1>
          {module.name} <Badge tone={toneForStatus(module.status)}>{STATUS_LABEL[module.status] ?? module.status}</Badge>
        </h1>
      </div>
      <Tabs
        items={[
          { key: "manifest", label: "Manifiesto" },
          { key: "plans", label: "Planes" },
          { key: "clients", label: "Clientes OAuth" },
          { key: "webhooks", label: "Webhooks" },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === "manifest" && <ManifestTab module={module} />}
      {tab === "plans" && <ModulePlans module={module} />}
      {tab === "clients" && <ModuleClients moduleCode={module.code} />}
      {tab === "webhooks" && <ModuleWebhooks moduleCode={module.code} />}
    </section>
  );
}
